/** Shared optional screenshot row for section/template reports (TR-3). */

import { createButton } from './components.js';
import { captureScreenFrame } from '../core/screen-capture.js';

async function sha256Hex(blob) {
  const bytes = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function createReportScreenshotControl({
  testid,
  capture = captureScreenFrame,
  onError = () => {}
}) {
  const wrap = document.createElement('div');
  wrap.className = 'pb-issuereport-artifact';
  wrap.dataset.testid = `${testid}-row`;
  const title = document.createElement('strong');
  title.textContent = 'Screenshot (optional)';
  const hint = document.createElement('p');
  hint.className = 'pb-set-row-hint';
  hint.textContent =
    'Capture one frame from a tab or window. You choose the source; no audio is recorded.';
  const status = document.createElement('div');
  status.className = 'pb-set-row-hint';
  status.dataset.testid = `${testid}-status`;

  let evidence = null;
  let objectUrl = null;
  const clearObjectUrl = () => {
    if (objectUrl && typeof URL?.revokeObjectURL === 'function') URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  };

  const captureButton = createButton({
    label: 'Capture current screen',
    variant: 'secondary',
    testid,
    busyLabel: 'Opening picker…',
    onAsyncClick: async () => {
      try {
        const blob = await capture();
        if (!blob) return; // decline is a choice, not an error
        const declaration = { bytes: blob.size, sha256: await sha256Hex(blob) };
        evidence = { blob, declaration };
        renderReady();
      } catch (err) {
        onError(err);
      }
    }
  });

  function renderReady() {
    clearObjectUrl();
    status.replaceChildren();
    const text = document.createElement('span');
    text.textContent = `Screenshot ready · ${evidence.declaration.bytes.toLocaleString()} bytes`;
    status.appendChild(text);
    if (typeof URL?.createObjectURL === 'function') {
      objectUrl = URL.createObjectURL(evidence.blob);
      const image = document.createElement('img');
      image.className = 'pb-issuereport-thumb';
      image.alt = 'Screenshot selected for this report';
      image.src = objectUrl;
      status.appendChild(image);
    }
    status.appendChild(
      createButton({
        label: 'Remove',
        variant: 'ghost',
        testid: `${testid}-remove`,
        onClick: () => {
          evidence = null;
          clearObjectUrl();
          status.replaceChildren();
        }
      })
    );
  }

  wrap.append(title, hint, captureButton, status);
  return {
    wrap,
    getEvidence: () => evidence,
    dispose: clearObjectUrl
  };
}
